import type React from "react";
import { Edit2, Trash2, Copy } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import type { ColorToken } from "../types";

interface ColorItemContextMenuProps {
  children: React.ReactNode;
  color: ColorToken;
  onRename: () => void;
  onDelete: () => void;
  onCopy?: () => void;
  isProtected: boolean;
  protectionReason?: string;
  showCopy?: boolean;
}

export const ColorItemContextMenu: React.FC<ColorItemContextMenuProps> = ({
  children,
  color,
  onRename,
  onDelete,
  onCopy,
  isProtected,
  protectionReason,
  showCopy = false,
}) => {
  const handleCopyColor = () => {
    if (onCopy) {
      onCopy();
    } else {
      // Default copy behavior - copy color value to clipboard
      navigator.clipboard.writeText(color.hexColor || color.value);
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="bg-background border-border text-text-primary"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <ContextMenuItem
          className={`text-xs hover:bg-[#2f3035] focus:bg-[#2f3035] ${
            isProtected ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            // Add small delay to let context menu fully close before starting rename
            setTimeout(() => onRename(), 200); // 150ms is the delay for the context menu to fully close
          }}
          disabled={isProtected}
          title={isProtected ? protectionReason : "Rename this color"}
        >
          <Edit2 className="w-3 h-3 mr-2" />
          Rename
        </ContextMenuItem>

        {showCopy && (
          <>
            <ContextMenuItem
              className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
              onClick={handleCopyColor}
              title="Copy color value to clipboard"
            >
              <Copy className="w-3 h-3 mr-2" />
              Copy Color
            </ContextMenuItem>
            <ContextMenuSeparator className="bg-[#2f3035]" />
          </>
        )}

        <ContextMenuItem
          className={`text-xs text-destructive hover:bg-[#2f3035] focus:bg-[#2f3035] ${
            isProtected ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isProtected}
          title={isProtected ? protectionReason : "Delete this color"}
        >
          <Trash2 className="w-3 h-3 mr-2" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
